import React, { FC, UIEvent, useCallback, useMemo, useState } from 'react';
import Svg from 'svg-inline-react';
import { debounce, throttle } from 'lodash';
import moment from 'moment';
import {
  GetVerifiedUserAvailableOpeningsForApplicationQuery,
  OpeningSourceEnum,
  OpeningStateEnum,
  SearchOpeningEntityTagInput,
  SearchOpeningEntityTagNameEnum,
  useGetVerifiedUserAvailableOpeningsForApplicationQuery,
} from '@src/graphql/generated';
import FrCloseSvg from '@src/svgs/fr-close.svg';
import { SelectSpeciality } from '@src/views/FeaturedApplicants/SelectSpeciality';
import { SearchField } from '@src/components/TextField';
import { DEFAULT_SPECIALITY_KEY } from '@src/views/FeaturedApplicants/constants/filters';
import { Image } from '@src/components/Image';
import { MEETFRANK_ADMIN_URL } from '@src/constants';
import { handleFetchMore } from '@src/util/handleFetchMore';
import { VirtualRecruiterOffersProps } from './props';
import './styles.scss';

const FIRST_BATCH_LIMIT = 10;
const NEXT_BATCH_LIMIT = 5;

export const VirtualRecruiterOffers: FC<VirtualRecruiterOffersProps> = ({ userId, onClose, onOpeningSelect }) => {
  const [searchPattern, setSearchPattern] = useState<string>();

  const [selectedSpecialityId, setSelectedSpecialityId] = useState<string>();

  const searchTagWithOrCondition = useMemo<SearchOpeningEntityTagInput[]>(() => {
    return [
      ...(selectedSpecialityId && selectedSpecialityId !== DEFAULT_SPECIALITY_KEY
        ? [
            {
              tag: SearchOpeningEntityTagNameEnum.SPECIALITY,
              value: selectedSpecialityId,
            },
          ]
        : []),
    ];
  }, [selectedSpecialityId]);

  const {
    data: verifiedUserAvailableOpeningsData,
    loading: isVerifiedUserAvailableOpeningsLoading,
    refetch: refetchVerifiedUserAvailableOpenings,
    fetchMore: fetchMoreVerifiedUserAvailableOpenings,
  } = useGetVerifiedUserAvailableOpeningsForApplicationQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      openingPaginationOptions: {
        limit: FIRST_BATCH_LIMIT,
      },
      openingSearchParams: {
        searchTagsWithAndCondition: [
          {
            tag: SearchOpeningEntityTagNameEnum.SOURCE_TYPE,
            value: OpeningSourceEnum.MEETFRANK,
          },
          {
            tag: SearchOpeningEntityTagNameEnum.STATE,
            value: OpeningStateEnum.ACTIVE,
          },
        ],
        searchTagsWithOrCondition: searchTagWithOrCondition,
      },
      userId: userId,
    },
  });

  const openings = verifiedUserAvailableOpeningsData?.verifiedUser.availableOpeningsForApplication.result;
  const pagination = verifiedUserAvailableOpeningsData?.verifiedUser.availableOpeningsForApplication.pagination;

  const onSearch = useCallback(
    debounce((searchValue: string) => {
      refetchVerifiedUserAvailableOpenings({
        openingSearchParams: {
          searchPattern: searchValue,
        },
      });
    }, 350),
    [],
  );

  const onChange = (value: string) => {
    setSearchPattern(value);
    onSearch(value);
  };

  const buildSubTitle = (titles: (string | number | undefined | null)[]) => {
    return titles.filter(Boolean).join(' • ');
  };

  const loadMore = useCallback(
    throttle(() => {
      if (!pagination) {
        return;
      }

      handleFetchMore(fetchMoreVerifiedUserAvailableOpenings, {
        updateQuery: (
          previousResult: GetVerifiedUserAvailableOpeningsForApplicationQuery,
          { fetchMoreResult }: { fetchMoreResult: GetVerifiedUserAvailableOpeningsForApplicationQuery },
        ) => {
          if (!fetchMoreResult) {
            return previousResult;
          }

          return {
            ...previousResult,
            verifiedUser: {
              ...previousResult.verifiedUser,
              availableOpeningsForApplication: {
                ...previousResult.verifiedUser.availableOpeningsForApplication,
                pagination: {
                  ...fetchMoreResult.verifiedUser.availableOpeningsForApplication.pagination,
                },
                result: [
                  ...previousResult.verifiedUser.availableOpeningsForApplication.result,
                  ...fetchMoreResult.verifiedUser.availableOpeningsForApplication.result,
                ],
              },
            },
          };
        },
        variables: {
          openingPaginationOptions: {
            limit: NEXT_BATCH_LIMIT,
            minCursor: pagination.maxCursor,
          },
        },
      });
    }, 200),
    [pagination, isVerifiedUserAvailableOpeningsLoading],
  );

  const handleScroll = (event: UIEvent) => {
    const { target } = event;

    if (target instanceof HTMLDivElement) {
      const scrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const offsetHeight = target.offsetHeight;

      const bottomPosition = scrollTop + offsetHeight;
      const distanceFromBottom = scrollHeight - bottomPosition;

      if (distanceFromBottom <= 100 && !isVerifiedUserAvailableOpeningsLoading && pagination?.hasMoreResults) {
        loadMore();
      } else {
        loadMore.cancel();
      }
    }
  };

  return (
    <div className="virtual-recruiter-offers">
      <div className="virtual-recruiter-offers__header">
        <div className="virtual-recruiter-offers__close" onClick={() => onClose()}>
          <Svg className="virtual-recruiter-offers__close-icon" src={FrCloseSvg} />
          Close
        </div>

        <SelectSpeciality
          className="virtual-recruiter-offers__speciality"
          labelClassName="virtual-recruiter-offers__speciality-label"
          onSelect={specialityId => setSelectedSpecialityId(specialityId)}
          selectedSpecialityId={selectedSpecialityId}
        />

        <SearchField
          className="virtual-recruiter-offers__search-field"
          onChange={onChange}
          onClear={() => onChange('')}
          placeholder="Search by title and company"
          value={searchPattern}
        />
      </div>

      <div className="virtual-recruiter-offers__list" onScroll={handleScroll}>
        {openings?.map(opening => {
          const companyLogo = opening.company?.profile.logo?.fullUrl;
          const positionLocation = opening.positionLocations?.[0];
          const location = `${positionLocation?.shortName}, ${positionLocation?.country?.shortName}`;
          const salaryFrom = opening.salaryRange?.from;
          const salaryTo = opening.salaryRange?.to;
          const salaryCurrency = opening.salaryRange?.currency?.symbol;
          const salaryPeriod = opening.salaryRange?.period;
          const salary = opening.salaryRange?.offerSalary;

          const salaryRange =
            salaryFrom && salaryTo && salaryCurrency && salaryPeriod
              ? `${salaryFrom} - ${salaryTo}${salaryCurrency}/${salaryPeriod}`
              : salary || undefined;

          const published = opening.publishedAt
            ? `Published ${moment(opening.publishedAt).format('DD.MM.YY')}`
            : undefined;

          return (
            <div className="virtual-recruiter-offers__item" key={opening.id}>
              {companyLogo && <Image className="virtual-recruiter-offers__item-logo" src={companyLogo} />}

              <div className="virtual-recruiter-offers__item-info">
                <div className="virtual-recruiter-offers__item-title">{opening.title}</div>

                <div className="virtual-recruiter-offers__item-details">
                  {buildSubTitle([opening.company?.profile.name, location, salaryRange, published])}
                </div>
              </div>

              <a
                className="virtual-recruiter-offers__item-action"
                href={MEETFRANK_ADMIN_URL + '/openings/' + opening.id}
                target="_blank"
                rel="noreferrer"
              >
                View in admin
              </a>

              <div
                className="virtual-recruiter-offers__item-action"
                onClick={() =>
                  onOpeningSelect({
                    openingId: opening.id,
                    openingTitle: opening.title,
                    pitch: opening.pitch,
                  })
                }
              >
                Select
              </div>
            </div>
          );
        })}

        {isVerifiedUserAvailableOpeningsLoading && <div className="virtual-recruiter-offers__loading">Loading...</div>}
      </div>
    </div>
  );
};
